// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useSession } from '@common/authorization/context/Session';
import { useMfaEnrollmentStatus } from '@portal/hooks/useMfaEnrollmentStatus';
import { Button } from '@imprivata-cloud/components';
import { getIdpLookupUrl } from '@utils/utils';
import { Tooltip } from 'antd';
import { CircleHelp, Settings } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { createHrefWithCurrentSearch } from '@utils/navigationSearch';
import { RootRoutes } from '..';
import UserMenu from '../components/dropdown/UserMenu';
import AppLauncher from '../components/layout/AppLauncher';
import PageHeader from '../components/layout/PageHeader';
import { CUSTOMER_EXPERIENCE_CENTER_URL, isAdminRole } from './constants';
import type { PortalSurface } from './PortalLayout';
import './PortalLayout.less';

type LandingPageHeaderProps = {
  userLogout: () => void;
  handleSettings?: () => void;
  surface: PortalSurface;
};

export enum TenantType {
  PRIMARY = 'PRIMARY',
  TEST = 'TEST',
}

export enum SelectValues {
  USER_UPN = 'user-upn',
  IMPRIVATA_IDENTITY = 'imprivata-identity',
  LOGOUT = 'Logout',
}

const TRANSLATION_ROOT = 'portal.landing.header';

const PRODUCT_LABEL: Partial<Record<PortalSurface, string>> = {
  settings: 'portal.settings.title',
  eamc: 'portal.eamc.product-name',
};

export const PortalHeader = ({ userLogout, handleSettings, surface }: LandingPageHeaderProps) => {
  const { tenantType, userInfo, tenantInfo } = useSession();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mfaStatus = useMfaEnrollmentStatus();
  const isMfaPastDeadline = mfaStatus?.isPastDeadline ?? false;
  const showSettings =
    isAdminRole(userInfo?.roleType) && !isMfaPastDeadline && surface !== 'settings';
  const productLabel = PRODUCT_LABEL[surface];
  const supportLabel = t('common.header.support');
  const settingsLabel = t('portal.settings.title');

  const headerSelectOptions = useMemo(
    () => [
      {
        key: SelectValues.IMPRIVATA_IDENTITY,
        label: t(`${TRANSLATION_ROOT}.select.imprivata-identity`),
      },
      { key: SelectValues.LOGOUT, label: t(`${TRANSLATION_ROOT}.select.logout`) },
    ],
    [t],
  );

  const handleImprivataIdentity = () => {
    const url = createHrefWithCurrentSearch(RootRoutes.SELF_SERVICE, searchParams);
    // deepcode ignore OR: URL is built from constant route paths and current search params. The startsWith('/') guard ensures only a relative URL reaches window.open.
    /* c8 ignore next */
    window.open(url.startsWith('/') ? url : RootRoutes.SELF_SERVICE, '_blank');
  };

  const redirectToTestTenant = () => {
    const testTenantId = tenantInfo?.relatedTestTenants[0]?.tenantId;
    if (testTenantId) {
      const idpUrl = getIdpLookupUrl(true, testTenantId);
      window.open(`${idpUrl}`, '_blank');
    }
  };

  const handleSelectChange = (value: string) => {
    switch (value) {
      case SelectValues.IMPRIVATA_IDENTITY:
        handleImprivataIdentity();
        break;
      case SelectValues.LOGOUT:
        userLogout();
        break;
      default:
        break;
    }
  };

  return (
    <>
      <div className="portal-header">
        <PageHeader
          className="portal-page-header"
          title={productLabel && t(productLabel)}
          rightSlot={
            <>
              {tenantType === TenantType.PRIMARY && !!tenantInfo?.relatedTestTenants?.length && (
                <Button type={'text'} onClick={redirectToTestTenant}>
                  Go to sandbox environment
                </Button>
              )}
              {tenantType === TenantType.TEST && <p>Sandbox use only</p>}

              {/* Support */}
              <Tooltip title={supportLabel} trigger={['hover', 'focus']}>
                <Button
                  aria-label={supportLabel}
                  className="header-icon-button"
                  href={CUSTOMER_EXPERIENCE_CENTER_URL}
                  icon={<CircleHelp size={24} aria-hidden />}
                  rel="noopener noreferrer"
                  target="_blank"
                  type="text"
                />
              </Tooltip>

              {/* Settings */}
              {showSettings && (
                <Tooltip title={settingsLabel} trigger={['hover', 'focus']}>
                  <Button
                    type="text"
                    icon={<Settings size={24} aria-hidden />}
                    className="header-icon-button"
                    onClick={handleSettings}
                    aria-label={settingsLabel}
                  />
                </Tooltip>
              )}

              {surface !== 'home' && <AppLauncher />}

              {/* User Menu */}
              <div className="profile-menu">
                <UserMenu
                  menu={{
                    items: headerSelectOptions,
                    onClick: ({ key }) => handleSelectChange(key),
                  }}
                  userUpn={userInfo?.userDisplayName || userInfo?.userUpn}
                  aria-label={'user-menu'}
                />
              </div>
            </>
          }
        />
      </div>

      {surface === 'page' && <div className="page-sub-header" />}
    </>
  );
};
